import React, {Fragment} from 'react'
import {connect} from 'react-redux'
import styles from './EditorColumn.scss'
import {GuidePageEditor} from './GuidePageEditor'
import * as guideActions from 'lib/api/experiences/guides'
import {Card} from 'common/styleguide/Card'
import {Icon} from 'common/Icon'
import {CTA} from 'common/styleguide/CTA'
import {CircleSpinner} from 'common/spinners/CircleSpinner'
import {editorStateFromDefault} from 'lib/draftUtils'
import {convertFromRaw, convertToRaw} from 'draft-js'
import p from 'prop-types'
import _ from 'lodash'
import smartDate from '@qrohlf/smart-date'

export class _EditorColumn extends React.PureComponent {
  static propTypes = {
    guideIds: p.array.isRequired
  }

  constructor (props) {
    super(props)

    this.state = {
      savePending: false,
      saveSuccess: false,
      saveError: false,
      lastSave: null,
      lastSaveStr: '',
      title: props.guide ? props.guide.title : '',
      showDeleteDialog: false,
      editorState: null
    }
  }

  componentDidUpdate (prevProps) {
    const prevId = prevProps.guide ? prevProps.guide.id : null
    const {guide} = this.props
    const nextId = guide ? guide.id : null

    if (prevId !== nextId) {
      const lastSave = guide ? {time: guide.updatedAt, auto: false} : null
      this.setState({
        title: guide ? guide.title : '',
        lastSave
      })
      this.getEditorState(true)
    }
  }

  componentDidMount () {
    // this interval is needed in order to force the `saved at` text to update.
    // (need to update state to rerun smartDate
    this._tickInterval = setInterval(this.tick, 30000)
  }

  componentWillUnmount () {
    clearInterval(this.tick)
  }

  tick = () => {
    const {lastSave} = this.state
    this.setState({lastSaveStr: lastSave ? smartDate(lastSave.time, {includeTime: false, relative: 'day'}) : ''})
  }

  inflateEditorStateFromContent = _.memoize((content) => (
    editorStateFromDefault(content ? convertFromRaw(content) : null)
  ))

  // lazy-load editorState
  getEditorState = (bust = false) => {
    const {guide} = this.props

    if (!guide) {
      return this.setState({editorState: null})
    }

    if (bust) {
      this.setState({editorState: null})
      return this.inflateEditorStateFromContent(guide.content)
    } else {
      return this.state.editorState || this.inflateEditorStateFromContent(guide.content)
    }
  }

  onSave = (isAutosave = true) => {
    const {updateGuidePage, guide} = this.props
    const {title} = this.state

    if (!guide) {
      return
    }

    const rawContentState = convertToRaw(this.getEditorState().getCurrentContent())

    this.setState({
      savePending: true,
      saveSuccess: false,
      saveError: false,
      lastSave: {time: new Date(), auto: isAutosave}
    })

    updateGuidePage(guide.set('content', rawContentState).set('title', title))
      .then(() => {
        this.setState({
          savePending: false,
          saveSuccess: true
        })
      })
      .catch(() =>
        this.setState({
          savePending: false,
          saveError: true
        })
      )
  }

  // autosave every second
  debouncedAutoSave = _.debounce(this.onSave, 1000, {leading: false, trailing: true})

  onTitleChange = ({title}) => {
    this.setState({title})
    // skip autosave if guide is live
    if (!this.props.guide.isLive) {
      this.debouncedAutoSave()
    }
  }

  onEditorChange = (editorState, cb) => {
    this.setState({editorState}, cb)
    // skip autosave if guide is live
    if (!this.props.guide.isLive) {
      this.debouncedAutoSave()
    }
  }

  render () {
    const {guideIds, guide} = this.props
    const {title, savePending, lastSave, lastSaveStr} = this.state

    return (
      <div className={styles.EditorColumn}>
        {!guide && <EmptyEditor guideIds={guideIds} />}
        {guide && <Fragment>
          <Card className={styles.editorCard}>
            {lastSave && <span className={styles.savedAt}>
              {lastSave.auto && 'auto'}saved {lastSaveStr || smartDate(lastSave.time, {includeTime: false, relative: 'day'})}
            </span>}

            <div className={styles.guideTitle}>
              <form onSubmit={e => { e.preventDefault(); this.onSave(false) }}>
                <input
                  type='text'
                  value={title}
                  placeholder='(Untitled)'
                  onChange={(e) => this.onTitleChange({title: e.target.value})}
                  className={styles.titleInput} />

                {/* 104px is the natural width of the button when displaying 'Save' */}
                {guide.isLive && <CTA button inline type='submit' style={{width: '104px'}} className={styles.liveSaveButton}>
                  {savePending
                    ? <CircleSpinner size={15} />
                    : 'Save'}
                </CTA>}
              </form>
            </div>
            <GuidePageEditor
              guide={guide}
              editorState={this.getEditorState()}
              onChange={this.onEditorChange}
              onSave={this.onSave}
              onCancel={this.onCancel} />
          </Card>
        </Fragment>}
      </div>
    )
  }
}

const EmptyEditor = ({guideIds}) => (
  <Card className={styles.emptyEditor}>
    <h1>{guideIds.length > 0 ? 'Select' : 'Add a'} guide section to edit</h1>
    <Icon i='md-map' size={120} />
  </Card>
)

const mapStateToProps = null

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateGuidePage: (guide) => dispatch(guideActions.updateGuidePage(guide))
})

export const EditorColumn = connect(
  mapStateToProps,
  mapDispatchToProps
)(_EditorColumn)
